import {useMemo, useState} from 'react'
import {StyleSheet, Text, View} from 'react-native'
import {Image} from 'expo-image'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {FormulaNotation} from '#/lib/flashbookTex/notation'
import {parseFormula} from '#/lib/flashbookTex/parse'
import {
  type FlashBookPlayPalette,
  getFlashBookPlayPalette,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {type Slide} from '#/types/capsule'
import {type FlashBookThemeTokens} from '#/types/flashbook'

/**
 * The *rich reading blocks* of a FlashBook page: quotations, notes/callouts,
 * image captions, tables and formulas.
 *
 * These are the blocks that a generic card wrapper destroys. A pull quote is not
 * a card. A formula on squared paper should be written on the paper. A data
 * table in a lab sheet is ruled cells, not a widget. So every one of them reads
 * its treatment from `design.presentation`, and the same content ends up
 * looking like something printed for *that* theme.
 *
 * Nothing here branches on a theme id — only on the declarative style names, so
 * a new theme gets all five treatments for free.
 */

/**
 * The page's line, for a theme that prints on paper that has lines on it.
 *
 * Zero for every other theme, which is what makes the two helpers below into
 * no-ops there — no other theme's treatments change.
 */
function rulingStep(design: FlashBookDesign): number {
  return design.block.mode === 'ruled'
    ? (design.page.textureStep ?? design.type.body.lineHeight)
    : 0
}

/**
 * A line height that is a whole number of the page's lines.
 *
 * A treatment is free to want slightly smaller or airier type than the role it
 * borrows (`body − 3`, `title + 6`), but on ruled paper it may not want a
 * fractional one: a line that is 23px tall in a 26px ruling puts the table's
 * second row, the caption and the note *between* the lines. So the preference
 * is honoured as closely as the paper allows, and never in halves.
 */
function onTheRule(preferred: number, design: FlashBookDesign): number {
  const step = rulingStep(design)
  if (step <= 0) return preferred
  return Math.max(step, Math.round(preferred / step) * step)
}

/**
 * An inset that is a whole number of the page's lines — zero included.
 *
 * For space *around* something rather than for a line of type: a 10px inset
 * above a formula written straight onto lined paper is ten pixels that push the
 * notation off the line it is written on, and the nearest whole line there is
 * none at all.
 */
function nearTheRule(preferred: number, design: FlashBookDesign): number {
  const step = rulingStep(design)
  if (step <= 0) return preferred
  return Math.round(preferred / step) * step
}

type Props = {
  block: Slide
  tokens?: FlashBookThemeTokens
  design: FlashBookDesign
  accent: string
}

/** Ink for a surface, so a tinted plate never ends up dark-on-dark. */
function inkOn(palette: FlashBookPlayPalette, tokens?: FlashBookThemeTokens) {
  return {
    heading: tokens?.textPrimary ?? palette.text,
    body: tokens?.textSecondary ?? palette.body,
    muted: tokens?.textMuted ?? palette.muted,
  }
}

export function FlashBookRichBlock({block, tokens, design, accent}: Props) {
  const pal = getFlashBookPlayPalette(tokens)
  const ink = inkOn(pal, tokens)

  switch (block.contentKind) {
    case 'quote':
      return (
        <Quote
          block={block}
          design={design}
          pal={pal}
          ink={ink}
          accent={accent}
        />
      )
    case 'callout':
      return (
        <Callout
          block={block}
          design={design}
          pal={pal}
          ink={ink}
          accent={accent}
        />
      )
    case 'caption':
      return (
        <Caption
          block={block}
          design={design}
          pal={pal}
          ink={ink}
          accent={accent}
        />
      )
    case 'table':
      return (
        <Table
          block={block}
          design={design}
          pal={pal}
          ink={ink}
          accent={accent}
        />
      )
    case 'formula':
      return (
        <Formula
          block={block}
          design={design}
          pal={pal}
          ink={ink}
          accent={accent}
        />
      )
    default:
      return null
  }
}

type Ink = {heading: string; body: string; muted: string}

// ─── Quote ───────────────────────────────────────────────────────────────────

function Quote({
  block,
  design,
  pal,
  ink,
  accent,
}: {
  block: Slide
  design: FlashBookDesign
  pal: FlashBookPlayPalette
  ink: Ink
  accent: string
}) {
  const style = design.presentation.quote
  const text = block.contentText ?? ''
  const author = block.contentAuthor?.trim()

  const body = (
    <Text
      style={[
        design.type.body,
        {
          color: ink.heading,
          // A quotation is set apart from the prose around it by *voice*, so it
          // is always italic — every theme keeps that, only the frame changes.
          fontStyle: style === 'bracket' ? 'normal' : 'italic',
          fontSize:
            (design.type.body.fontSize ?? 16) + (style === 'pull' ? 2 : 0),
        },
      ]}>
      {style === 'bracket' ? `[ ${text} ]` : text}
    </Text>
  )

  const authorLine = author ? (
    <Text
      style={[
        design.type.caption,
        {
          color: ink.muted,
          // An attribution is a line *of the page*, so on ruled paper it takes
          // a whole line above it: an 8px inset is eight pixels the ruling does
          // not have, and it would push the signature — and the reading control
          // under it — between two lines for the rest of the block.
          marginTop: onTheRule(8, design),
          fontStyle: 'normal',
          textTransform: style === 'plate' ? 'uppercase' : undefined,
        },
      ]}>
      {author}
    </Text>
  ) : null

  switch (style) {
    case 'flourish':
      return (
        <View style={qx.wrap}>
          <Rule color={accent} />
          {body}
          <Rule color={accent} />
          {authorLine}
        </View>
      )
    case 'margin':
      return (
        <View style={qx.marginWrap}>
          <View
            style={[qx.marginRule, {backgroundColor: accent, opacity: 0.7}]}
          />
          <View style={qx.marginBody}>
            {body}
            {authorLine}
          </View>
        </View>
      )
    case 'bar':
      return (
        <View style={qx.barWrap}>
          <View style={[qx.bar, {backgroundColor: accent}]} />
          <View style={qx.barBody}>
            {body}
            {authorLine}
          </View>
        </View>
      )
    case 'pull':
      return (
        <View style={qx.wrap}>
          <Text style={[qx.glyph, {color: accent}]}>&ldquo;</Text>
          <View
            style={[qx.pullRule, {backgroundColor: ink.muted, opacity: 0.4}]}
          />
          {body}
          {authorLine}
        </View>
      )
    case 'plate':
      return (
        <View
          style={[
            qx.plate,
            {backgroundColor: pal.surfaceAlt, borderColor: pal.border},
          ]}>
          {body}
          {authorLine}
        </View>
      )
    case 'bracket':
    default:
      return (
        <View style={qx.wrap}>
          {body}
          {authorLine}
        </View>
      )
  }
}

function Rule({color}: {color: string}) {
  return (
    <View style={qx.ruleRow}>
      <View style={[qx.ruleLine, {backgroundColor: color, opacity: 0.5}]} />
      <View style={[qx.ruleDot, {backgroundColor: color, opacity: 0.7}]} />
      <View style={[qx.ruleLine, {backgroundColor: color, opacity: 0.5}]} />
    </View>
  )
}

// ─── Callout ─────────────────────────────────────────────────────────────────

function toneColor(pal: FlashBookPlayPalette, tone?: string) {
  switch (tone) {
    case 'success':
      return pal.positive
    case 'warning':
      return pal.warning
    default:
      return pal.accent
  }
}

function toneIcon(tone?: string): 'info' | 'check-circle' | 'alert-triangle' {
  switch (tone) {
    case 'success':
      return 'check-circle'
    case 'warning':
      return 'alert-triangle'
    default:
      return 'info'
  }
}

function Callout({
  block,
  design,
  pal,
  ink,
  accent,
}: {
  block: Slide
  design: FlashBookDesign
  pal: FlashBookPlayPalette
  ink: Ink
  accent: string
}) {
  const {t: l} = useLingui()
  const style = design.presentation.callout
  const tone = block.calloutTone
  const mark = toneColor(pal, tone) ?? accent
  const title = block.contentTitle?.trim()
  const text = block.contentText ?? ''
  /**
   * Whether this callout is printed on a ruled sheet.
   *
   * The note's tab is a whole line of the page — the band a teacher sticks
   * across the rule — and the note under it starts on the next one. The tab's
   * own 10px of padding and the body's 11px and 14px insets are the kind of
   * fractional measure that leaves a note box straddling two lines with its
   * text written between them.
   */
  const step = rulingStep(design)
  const onPaper = step > 0

  const label = (
    <Text
      style={[
        design.type.caption,
        {
          color: mark,
          textTransform: 'uppercase',
          // A caption role can be *half* a line of the paper — squared paper
          // sets its kickers at 12px on a 24px grid — and half a line of label
          // above a note pushes that note, and everything under it, off the
          // ruling. On paper the label takes a whole line.
          lineHeight: step > 0 ? step : design.type.caption.lineHeight,
          // The box's own label sits *on* the rule above its note, not 5px off
          // it: on ruled paper this inset is what puts the note — and every
          // control below it — between two lines.
          marginBottom: nearTheRule(5, design),
        },
      ]}>
      {title ??
        (tone === 'warning'
          ? l`Careful`
          : tone === 'success'
            ? l`Good to know`
            : l`Note`)}
    </Text>
  )

  const body = <Text style={[design.type.body, {color: ink.body}]}>{text}</Text>

  /**
   * A framed note, laid on the ruling.
   *
   * Its edge is a drawn box, so both edges have to close on a line of the page
   * — a note that starts three pixels above one and ends two below the next is
   * the seam that makes a printed page look like a web page. The frame's own
   * 1px border is part of the box, so a whole line of inset is `step − 1` per
   * side: border + inset = exactly one rule, and the note inside starts on a
   * line rather than an inch under one.
   */
  const framedInset = step > 0 ? {padding: step - 1} : undefined

  switch (style) {
    case 'tint':
      return (
        <View style={[qx.tint, framedInset, {backgroundColor: mark + '1f'}]}>
          {label}
          {body}
        </View>
      )
    case 'outline':
      return (
        <View style={[qx.outline, framedInset, {borderColor: mark + '66'}]}>
          {label}
          {body}
        </View>
      )
    case 'sidebar':
      return (
        <View style={qx.barWrap}>
          <View style={[qx.bar, {backgroundColor: mark}]} />
          <View style={qx.barBody}>
            {label}
            {body}
          </View>
        </View>
      )
    case 'badge':
      return (
        <View style={qx.badgeRow}>
          <View style={[qx.badge, {borderColor: mark}]}>
            <Feather name={toneIcon(tone)} size={13} color={mark} />
          </View>
          <View style={qx.badgeBody}>
            {label}
            {body}
          </View>
        </View>
      )
    case 'tape':
    default:
      // The label is a *tab* set into the top-left of the box, flush with its
      // edge. A label that overhangs the frame was tried first and read as the
      // block spilling out of its own container — the one thing a callout must
      // never look like.
      return (
        <View style={[qx.tapeBox, {borderColor: mark + '55'}]}>
          <View
            style={[
              qx.tape,
              {backgroundColor: mark},
              onPaper && {minHeight: step, justifyContent: 'center'},
            ]}>
            <Text style={[design.type.caption, {color: pal.onAccent}]}>
              {title ??
                (tone === 'warning'
                  ? l`Careful`
                  : tone === 'success'
                    ? l`Good to know`
                    : l`Note`)}
            </Text>
          </View>
          <View
            style={[
              qx.tapeBody,
              onPaper && {
                paddingTop: onTheRule(11, design),
                paddingBottom: onTheRule(14, design),
              },
            ]}>
            <Text style={[design.type.body, {color: ink.body}]}>{text}</Text>
          </View>
        </View>
      )
  }
}

// ─── Image caption ───────────────────────────────────────────────────────────

function Caption({
  block,
  design,
  pal,
  ink,
  accent,
}: {
  block: Slide
  design: FlashBookDesign
  pal: FlashBookPlayPalette
  ink: Ink
  accent: string
}) {
  const style = design.presentation.caption
  const caption = block.contentText ?? ''
  const alt = block.contentTitle?.trim() || caption

  /**
   * A figure is a plate *pasted onto* the page, so its edges follow the lines
   * the way the caption's writing does. A 16:10 plate is 232px tall on a 26px
   * ruling, which leaves its bottom edge — and the caption under it — between
   * two lines; cropping it to a whole 234px puts both back on the paper.
   * Content still wins: what stays is the frame, not the exact ratio.
   */
  const step = rulingStep(design)
  const [figW, setFigW] = useState(0)
  const plateHeight =
    step > 0 && figW > 0
      ? Math.max(step, Math.round(figW / (16 / 10) / step) * step)
      : 0

  return (
    <View>
      {!!block.imageUrl && (
        <Image
          source={{uri: block.imageUrl}}
          style={[
            qx.figure,
            {borderColor: pal.border},
            plateHeight > 0 && {
              height: plateHeight,
              // One pixel per side is a pixel the ruling does not have.
              borderWidth: 0,
            },
          ]}
          onLayout={e => setFigW(e.nativeEvent.layout.width)}
          contentFit="cover"
          accessibilityLabel={alt}
          // The caption below is real text, so the figure itself only needs to
          // be *described* — this is the description, not a duplicate of it.
          accessibilityHint={caption || alt}
        />
      )}
      {!!caption && (
        <View style={style === 'kicker' ? qx.kickerRow : undefined}>
          {style === 'kicker' && (
            // On paper the kicker's own rule is written *on* one of the page's
            // lines: the short bar and its 10px of air are 12px, which is half
            // a line of a 24px grid and not even a third of a 26px one — and
            // that half-line is what demoted the caption under it, and the
            // reading control under that, to the space between two rules. So
            // the rule takes a whole line and draws its bar in the middle of
            // it; off ruled paper it keeps its own 2px, sitting 10px up.
            <View
              style={[
                qx.kickerRule,
                step > 0 && {
                  height: step,
                  marginTop: 0,
                  justifyContent: 'center',
                },
              ]}>
              <View
                style={{
                  height: 2,
                  width: 22,
                  borderRadius: 1,
                  backgroundColor: accent,
                }}
              />
            </View>
          )}
          <Text
            style={[
              style === 'small-caps' ? design.type.caption : design.type.body,
              {
                color: style === 'soft' ? ink.muted : ink.body,
                // The credit under a plate is written on the first line below
                // it, so it takes no inset at all on ruled paper.
                marginTop: nearTheRule(8, design),
                fontStyle: style === 'italic' ? 'italic' : 'normal',
                fontSize:
                  style === 'small-caps'
                    ? design.type.caption.fontSize
                    : (design.type.body.fontSize ?? 16) - 2,
                lineHeight:
                  style === 'small-caps'
                    ? design.type.caption.lineHeight
                    : onTheRule(
                        (design.type.body.lineHeight ?? 26) - 3,
                        design,
                      ),
                textTransform:
                  style === 'small-caps' || style === 'kicker'
                    ? 'uppercase'
                    : undefined,
              },
            ]}>
            {caption}
          </Text>
        </View>
      )}
    </View>
  )
}

// ─── Table ───────────────────────────────────────────────────────────────────

function Table({
  block,
  design,
  pal,
  ink,
  accent,
}: {
  block: Slide
  design: FlashBookDesign
  pal: FlashBookPlayPalette
  ink: Ink
  accent: string
}) {
  const style = design.presentation.table
  const rows = block.tableRows ?? []
  const cols = block.tableCols ?? rows[0]?.length ?? 0
  if (rows.length === 0 || cols === 0) return null

  const cellBorder = (isHeader: boolean): object => {
    switch (style) {
      case 'grid':
        return {
          borderWidth: StyleSheet.hairlineWidth * 2,
          borderColor: pal.border,
        }
      case 'outline':
        return {
          borderBottomWidth: isHeader ? StyleSheet.hairlineWidth * 2 : 0,
          borderBottomColor: pal.border,
        }
      case 'zebra':
        return {
          borderBottomWidth: isHeader ? StyleSheet.hairlineWidth * 2 : 0,
          borderBottomColor: accent,
        }
      case 'open':
        return {
          borderBottomWidth: isHeader ? StyleSheet.hairlineWidth * 2 : 0,
          borderBottomColor: accent,
        }
      // ruled
      default:
        return {
          borderBottomWidth: StyleSheet.hairlineWidth * 2,
          borderBottomColor: isHeader ? ink.heading : pal.border,
        }
    }
  }

  return (
    <View
      style={
        style === 'outline'
          ? [qx.table, {borderWidth: 1, borderColor: pal.border}]
          : style === 'grid'
            ? qx.table
            : undefined
      }>
      {rows.map((row, r) => {
        const isHeader = r === 0
        const zebra = style === 'zebra' && r > 0 && r % 2 === 1
        return (
          <View
            key={r}
            style={[
              qx.tr,
              isHeader && style === 'zebra'
                ? {backgroundColor: accent + '1f'}
                : zebra
                  ? {backgroundColor: pal.surfaceAlt}
                  : null,
            ]}>
            {Array.from({length: cols}, (_, c) => (
              <View
                key={c}
                style={[
                  qx.td,
                  cellBorder(isHeader),
                  // On ruled paper a row of a table is one line of the page:
                  // the cell's own 9px padding would put every row after the
                  // first between two lines.
                  rulingStep(design) > 0 && {
                    paddingVertical: nearTheRule(9, design),
                  },
                ]}>
                <Text
                  style={[
                    isHeader ? design.type.caption : design.type.label,
                    {
                      color: isHeader ? ink.heading : ink.body,
                      textTransform: isHeader ? 'uppercase' : undefined,
                      fontWeight: isHeader ? '800' : undefined,
                      // A header that is not a whole line tall puts every row
                      // below it between two lines of the page.
                      lineHeight: onTheRule(
                        (isHeader ? design.type.caption : design.type.label)
                          .lineHeight,
                        design,
                      ),
                    },
                  ]}>
                  {row[c] ?? ''}
                </Text>
              </View>
            ))}
          </View>
        )
      })}
    </View>
  )
}

// ─── Formula ─────────────────────────────────────────────────────────────────

function Formula({
  block,
  design,
  pal,
  ink,
  accent,
}: {
  block: Slide
  design: FlashBookDesign
  pal: FlashBookPlayPalette
  ink: Ink
  accent: string
}) {
  const style = design.presentation.formula
  const source = block.latex ?? ''
  const note = block.contentText?.trim()

  /*
   * The notation is *typeset*, not printed: read into structure, measured, and
   * drawn as an SVG (see `#/lib/flashbookTex`). It used to be a string in a
   * `Text`, which is why the block could not draw a fraction — `(x)⁄(y)` is what
   * a flat string has instead of a bar, and it has nowhere to put a second one.
   *
   * The size the theme asks for is the size the formula is set at, and the face
   * is the book's own, so a formula in a notebook is written in the notebook's
   * hand. The theme's `chalk`/`plate`/`boxed`/`inline` treatments below are
   * untouched: they are about the *surface* the notation sits on, and a formula
   * printed on a plate is still printed on a plate.
   */
  const parsed = useMemo(() => parseFormula(source), [source])
  const glyph = source.trim() ? (
    <FormulaNotation
      testID="flashbookFormulaNotation"
      node={parsed.node}
      size={(design.type.title.fontSize ?? 20) + 1}
      color={style === 'chalk' || style === 'inline' ? accent : ink.heading}
      fontFamily={design.type.body.fontFamily}
      align={style === 'inline' ? 'flex-start' : 'center'}
      baselineStep={rulingStep(design)}
    />
  ) : null

  const framed = (
    <>
      {glyph}
      {!!note && (
        <Text
          style={[
            design.type.caption,
            {
              color: ink.muted,
              textAlign: 'center',
              marginTop: nearTheRule(6, design),
            },
          ]}>
          {note}
        </Text>
      )}
    </>
  )

  /**
   * Frames carry the notation, so their leading is a whole number of lines too:
   * on ruled paper a 14px inset starts the formula half a line down, and the
   * one thing a formula on lined paper must look like is *written on the line*.
   */
  const framePad =
    rulingStep(design) > 0
      ? style === 'chalk' || style === 'inline'
        ? {paddingVertical: nearTheRule(10, design)}
        : {paddingVertical: nearTheRule(16, design)}
      : null

  switch (style) {
    case 'plate':
      return (
        <View
          style={[
            qx.plate,
            {backgroundColor: pal.surfaceAlt, borderColor: pal.border},
            framePad,
          ]}>
          {framed}
        </View>
      )
    case 'boxed':
      return (
        <View style={[qx.formulaBox, {borderColor: pal.border}, framePad]}>
          {framed}
        </View>
      )
    case 'inline':
      return <View style={[qx.formulaInline, framePad]}>{framed}</View>
    // chalk — written straight on the page, no container at all
    default:
      return <View style={[qx.formulaChalk, framePad]}>{framed}</View>
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const qx = StyleSheet.create({
  wrap: {width: '100%'},

  // flourish
  ruleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginVertical: 10,
  },
  ruleLine: {flex: 1, height: StyleSheet.hairlineWidth * 2},
  ruleDot: {width: 4, height: 4, borderRadius: 2},

  // margin
  marginWrap: {flexDirection: 'row', width: '100%'},
  marginRule: {width: 2, marginRight: 12, borderRadius: 1},
  marginBody: {flex: 1},

  // bar / sidebar
  barWrap: {flexDirection: 'row', width: '100%'},
  bar: {width: 3, borderRadius: 2, marginRight: 13},
  barBody: {flex: 1},

  // pull
  glyph: {fontSize: 46, lineHeight: 42, fontWeight: '900', opacity: 0.35},
  pullRule: {height: StyleSheet.hairlineWidth * 2, marginBottom: 10},

  // plate
  plate: {
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 16,
    paddingHorizontal: 16,
    width: '100%',
  },

  // callout
  tint: {borderRadius: 12, padding: 14, width: '100%'},
  outline: {borderWidth: 1, borderRadius: 12, padding: 14, width: '100%'},
  badgeRow: {flexDirection: 'row', width: '100%'},
  badge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  badgeBody: {flex: 1},
  tapeBox: {
    borderWidth: 1,
    borderRadius: 10,
    width: '100%',
    // Keeps the tab's own corners inside the box's radius.
    overflow: 'hidden',
  },
  tape: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  tapeBody: {paddingHorizontal: 15, paddingTop: 11, paddingBottom: 14},

  // caption
  figure: {
    width: '100%',
    aspectRatio: 16 / 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  kickerRow: {width: '100%'},

  kickerRule: {height: 2, width: 22, marginTop: 10},

  // table
  table: {width: '100%', borderRadius: 2, overflow: 'hidden'},
  tr: {flexDirection: 'row', width: '100%'},
  td: {flex: 1, paddingVertical: 9, paddingHorizontal: 10},

  // formula
  formulaBox: {
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 14,
    width: '100%',
  },
  formulaChalk: {paddingVertical: 10, width: '100%'},
  formulaInline: {width: '100%'},
  formulaTxt: {fontWeight: '700'},
})
